// OFFLINE-UX — whole file. See DU.md §33 group C; `grep -rn "OFFLINE-UX" src/`.
import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import WifiOffIcon from "@mui/icons-material/WifiOff";

/**
 * Tells the agent they are offline, app-wide, from the root.
 *
 * A strip rather than a toast: being offline is a STATE, and a toast dismisses
 * itself while the state is still true. It sits above everything and stays
 * until the connection returns.
 *
 * It deliberately does NOT block interaction. An agent is usually on a live
 * call, and most of what they need offline still works — reading the ticket
 * already on screen, scrolling, switching tabs, closing a dialog, copying a
 * caller's ID onto paper. Freezing the UI would make them less able to help.
 * What stops instead are the WRITES, which fail immediately with a clear reason
 * (`isOffline` in shared/services/shared.js) rather than hanging for seventeen
 * seconds and then failing anyway.
 *
 * `document.body.dataset.offline` is set as well, so a stylesheet can dim
 * individual controls later without this component changing:
 *
 *     body[data-offline="true"] [data-needs-network] {
 *       pointer-events: none;
 *       opacity: 0.5;
 *     }
 */
const OfflineGuard = () => {
  // `navigator.onLine === false` is the reliable direction. True can still mean
  // a captive portal, so this never claims the network is WORKING — only that
  // the browser is certain it is not.
  const [offline, setOffline] = useState(() => navigator.onLine === false);

  useEffect(() => {
    const update = () => setOffline(navigator.onLine === false);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    if (offline) {
      document.body.dataset.offline = "true";
    } else {
      delete document.body.dataset.offline;
    }
    return () => {
      delete document.body.dataset.offline;
    };
  }, [offline]);

  if (!offline) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        // Above MUI's modal layer (1300) so it stays visible over a dialog.
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        py: 0.75,
        px: 2,
        // Light grey at the top fading to white at the bottom, so the strip
        // reads as sitting above the page rather than being part of it.
        background: "linear-gradient(180deg, #e8e8e8 0%, #ffffff 100%)",
        color: "#424242",
        border: "1px solid #d0d0d0",
        // Only the bottom edge reads as a border when the strip is flush to the
        // top of the viewport; the other three would show as stray hairlines.
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
      }}
    >
      <WifiOffIcon fontSize="small" sx={{ color: "#616161" }} />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          /**
           * A slow fade, NOT a hard on/off blink.
           *
           * WCAG 2.3.1 caps flashing at three times a second — a real blink sits
           * near that line and is a seizure risk. A 1.6s opacity cycle that
           * never reaches zero draws the eye without ever fully hiding the
           * words, which matters because this is the only thing telling the
           * agent their saves are not landing.
           */
          animation: "bbhOfflinePulse 1.6s ease-in-out infinite",
          "@keyframes bbhOfflinePulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.45 },
          },
          // Honour the OS setting. Someone who has asked for less motion still
          // needs to read this, so it simply stops moving.
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        You&apos;re offline — anything you save now will not reach the server.
      </Typography>
    </Box>
  );
};

export default OfflineGuard;
